/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {NgbDate, NgbDropdown, NgbDropdownConfig} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormBuilder, UntypedFormControl} from '@angular/forms';
import {Subscription} from 'rxjs';
import {DateFilterOptions} from './date-filter-options.enum';

import moment, {Moment} from 'moment';
import {AbstractFilterComponent} from '../abstract-filter.component';
import {map} from 'rxjs/operators';
import {InputDateComponent} from '../../input/input-date/input-date.component';
import {DateFilterStateModel} from './date-filter-state.model';

@Component({
    selector: 'app-date-filter',
    templateUrl: './date-filter.component.html',
    styleUrls: ['../filter.component.css'],
    providers: [NgbDropdownConfig],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DateFilterComponent extends AbstractFilterComponent<DateFilterStateModel> implements OnInit, OnDestroy {
    @ViewChild('dateDropdown', {static: true}) dateDropdown: NgbDropdown;
    @ViewChild('pickDate', {static: true}) pickDate: ElementRef<HTMLInputElement>;
    @ViewChild('pickDateInput', {static: true}) pickDateInput: InputDateComponent;
    @ViewChild('customRange', {static: true}) customRange: ElementRef<HTMLInputElement>;
    @ViewChild('pickDateInputCustomStart', {static: true}) pickDateStart: InputDateComponent;
    @ViewChild('pickDateInputCustomEnd', {static: true}) pickDateEnd: InputDateComponent;
    @ViewChild('allDates', {static: true}) allDates: ElementRef<HTMLInputElement>;

    @Input() dateDisplayedInUtc: boolean;
    @Input() hasEmptyOption = false;

    public selectedDateFilter = this.formBuilder.group({
        selectedDate: new UntypedFormControl(),
    });

    DateFilterOptions = DateFilterOptions;
    private subscriptions = new Subscription();

    constructor(private formBuilder: UntypedFormBuilder) {
        super();
    }

    public convertNgbDateToMoment(ngbDate: NgbDate): Moment {
        if (this.dateDisplayedInUtc) {
            return moment(`${ngbDate.year}-${ngbDate.month}-${ngbDate.day}`, 'YYYY-MM-DD');
        } else {
            return moment.utc(`${ngbDate.year}-${ngbDate.month}-${ngbDate.day}`, 'YYYY-MM-DD');
        }
    }

    public convertNumberToDateObject(number: number): object {
        if (number == null) {
            return null;
        }

        const momentInstance = this.dateDisplayedInUtc ? moment(number) : moment.utc(number);

        return {
            year: momentInstance.year(),
            month: momentInstance.month() + 1,
            day: momentInstance.date()
        };
    }

    ngOnInit(): void {
        this.selectedDateFilter.get('selectedDate').setValue(DateFilterOptions.ALL);
        super.storeState();
        /** Filter updates **/
        this.subscriptions.add(this.selectedDateFilter.valueChanges
                .pipe(map(value => value.selectedDate))
                .subscribe(this.dateFilterUpdate));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /** Update the filter value when the filter value in the dropdown changes **/
    dateFilterUpdate = (selectedDate: DateFilterOptions): void => {
        this.storeState({
            option: selectedDate
        });
        /** Set the date filter**/
        switch (selectedDate) {
            case DateFilterOptions.ALL:
                this.setAllDates();
                break;
            case DateFilterOptions.PASTDAY:
                this.setPastDayDate();
                break;
            case DateFilterOptions.PASTWEEK:
                this.setPastWeekDate();
                break;
            case DateFilterOptions.PASTMONTH:
                this.setPastMonthDate();
                break;
            case DateFilterOptions.PASTYEAR:
                this.setPastYearDate();
                break;
            case DateFilterOptions.EMPTY:
                this.setEmptyDate();
                break;
            case DateFilterOptions.CUSTOM:
                this.setCustomDate();
                break;
        }
    };

    /** Emit the date filter value when dropdown is closed **/
    emitSearch(): void {
        this.storeState();

        if ((this.pickDate.nativeElement.checked || this.customRange.nativeElement.checked)
                && this.state.start == null && this.state.end == null) {
            this.resetFilter();
        }

        this.filterEvent.emit(this.state);
    }

    setAllDates(): void {
        this.storeState({
            empty: null,
            end: null,
            start: null,
        });
    }

    setDate(date: NgbDate): void {
        this.storeState({empty: null});
        this.setStartDate(date);
        this.setEndDate(date);
    }

    setStartDate(date: NgbDate): void {
        if (date != null) {
            this.storeState({start: this.convertNgbDateToMoment(date).valueOf()});
        } else {
            this.storeState({start: null});
        }

        if (!Number.isSafeInteger(this.state.start)) {
            this.storeState({start: null});
        }
    }

    setEndDate(date: NgbDate): void {
        if (date != null) {
            this.storeState({
                end: this.convertNgbDateToMoment(date)
                        .add(1, 'days')
                        .subtract(1, 'ms')
                        .valueOf()
            });
        } else {
            this.storeState({end: null});
        }

        if (!Number.isSafeInteger(this.state.end)) {
            this.storeState({end: null});
        }
    }

    /** Set changedOnFilter with past day date range **/
    setPastDayDate(): void {
        const date = this.dateDisplayedInUtc ? moment.utc() : moment();
        this.storeState({
            empty: null,
            end: date.valueOf(),
            start: date.subtract(1, 'days').valueOf()
        });
    }

    /** Set changedOnFilter with past week date range **/
    setPastWeekDate(): void {
        const date = this.dateDisplayedInUtc ? moment.utc() : moment();
        this.storeState({
            empty: null,
            end: date.valueOf(),
            start: date.subtract(1, 'weeks').valueOf()
        });
    }

    /** Set changedOnFilter with past month date range **/
    setPastMonthDate(): void {
        const date = this.dateDisplayedInUtc ? moment.utc() : moment();
        this.storeState({
            empty: null,
            end: date.valueOf(),
            start: date.subtract(1, 'months').valueOf()
        });
    }

    /** Set changedOnFilter with past year date range **/
    setPastYearDate(): void {
        const date = this.dateDisplayedInUtc ? moment.utc() : moment();
        this.storeState({
            empty: null,
            end: date.valueOf(),
            start: date.subtract(1, 'years').valueOf()
        });
    }

    setEmptyDate(): void {
        this.storeState({
            empty: true,
            start: null,
            end: null,
        });
    }

    setCustomDate(): void {
        this.pickDateStart.reset();
        this.pickDateEnd.reset();
    }

    onDateFocus(): void {
        this.focusRadioButton(this.pickDate.nativeElement);
    }

    onDateChange(event: any): void {
        this.setDate(event);
    }

    onStartDateFocus(): void {
        this.focusRadioButton(this.customRange.nativeElement);
    }

    onStartDateChange(event: any): void {
        this.setStartDate(event);
    }

    onEndDateFocus(): void {
        this.focusRadioButton(this.customRange.nativeElement);
    }

    onEndDateChange(event: any): void {
        this.setEndDate(event);
    }

    /** Set the focus to the radio button after date is selected in the datepicker **/
    focusRadioButton(radioButton: HTMLInputElement): void {
        radioButton.click();
    }

    resetFilter(): void {
        this.selectedDateFilter.setValue({selectedDate: DateFilterOptions.ALL});
        this.pickDateInput.deleteInputValue();
        this.setAllDates();
    }

    initFilter(state: DateFilterStateModel): void {
        if (state != null) {
            this.storeState(state);

            this.selectedDateFilter.get('selectedDate').setValue(state.option);
            this.dateFilterUpdate(state.option);

            if (state.option === DateFilterOptions.PICKDATE) {
                const date = this.convertNumberToDateObject(state.start);
                this.pickDateInput.setValue(date);
            } else if (state.option === DateFilterOptions.CUSTOM) {
                const endDate = this.convertNumberToDateObject(state.end);
                this.pickDateEnd.setValue(endDate);
                const startDate = this.convertNumberToDateObject(state.start);
                this.pickDateStart.setValue(startDate);
            }
        }
    }

    storeState(state?: Partial<DateFilterStateModel>): void {
        super.storeState(state);
        super.storeState({
            isActive: this.state.start != null || this.state.end != null || this.state.empty != null
        });
    }
}
